import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef } from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { ConfigService, MLService } from 'app/services';
import { CustomWindow } from 'app/@types';

declare let window: CustomWindow;

@Component({
    selector: 'home-settings',
    templateUrl: './home-settings.component.html',
    styleUrls: ['./home-settings.component.scss'],
    providers: [MessageService]
})
export class HomeSettingsComponent implements OnInit, OnDestroy {
    isShowTestExceptionButton = window['isShowTestExceptionButton'];

    @Input()
    public menuPermissionActive!: string;
    public breadcrumbs: MenuItem[] = [{ icon: 'icon_settings' }, { label: 'Settings' }];

    public selectedPdf: File | null = null;
    public isUploading: boolean = false;
    public uploadedPdfUrl: string | null = null;

    @ViewChild('pdfInput') pdfInput!: ElementRef<HTMLInputElement>;

    // Store timeout IDs for cleanup
    private pollingTimeoutId: ReturnType<typeof setTimeout> | null = null;
    private retryTimeoutIds: ReturnType<typeof setTimeout>[] = [];

    constructor(
        public configService: ConfigService,
        private messageService: MessageService,
        private mlService: MLService) {
    }

    ngOnInit(): void {
    }

    ngOnDestroy(): void {
        // Clear initial polling timeout
        if (this.pollingTimeoutId !== null) {
            clearTimeout(this.pollingTimeoutId);
            this.pollingTimeoutId = null;
        }

        // Clear all retry timeouts
        this.retryTimeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
        this.retryTimeoutIds = [];
    }

    generateTestException() {
        throw new Error('Frontend exception test');
    }

    onPdfSelected(event: Event): void {
        const target = event.target as HTMLInputElement;
        const file = target.files?.[0];

        if (!file) {
            return;
        }

        // Validate file type
        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Please select a valid PDF file'
            });
            return;
        }

        // Validate file size
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (file.size > maxSize) {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'PDF file size should not exceed 10MB'
            });
            return;
        }

        this.selectedPdf = file;
        this.uploadedPdfUrl = null;

        this.uploadPdf();
    }

    removePdf(): void {
        this.selectedPdf = null;
        this.uploadedPdfUrl = null;
        if (this.pdfInput && this.pdfInput.nativeElement) {
            this.pdfInput.nativeElement.value = '';
        }
    }

    uploadPdf(): void {
        if (!this.selectedPdf) {
            return;
        }

        const formData = new FormData();
        formData.append('formFile', this.selectedPdf);

        this.isUploading = true;

        this.mlService.uploadReceiptPdf(formData, this.messageService)
            .then(data => {
                if (!data) {
                    this.isUploading = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to upload PDF'
                    });
                    return;
                }

                if (data.Success && data.PdfUrl) {
                    const expectedCsvUrl = data.PdfUrl.replace(/\.pdf$/i, '.csv');
                    const originalFileName = this.selectedPdf?.name || 'receipt';

                    // Show immediate feedback that PDF upload succeeded
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Upload Successful',
                        detail: 'PDF uploaded successfully. Receipt parsing started in background. CSV will be downloaded automatically when ready.'
                    });

                    // Start polling after 1 minute delay
                    this.pollingTimeoutId = setTimeout(() => {
                        this.pollAndDownloadCsv(expectedCsvUrl, originalFileName);
                    }, 60000);
                } else {
                    this.isUploading = false;
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: data.ErrorMessage || 'Failed to upload PDF'
                    });
                }
            })
            .catch(err => {
                this.isUploading = false;
                const errorMessage = err.message === 'Request timeout'
                    ? 'Request timeout: The operation is taking longer than expected. Please check back later.'
                    : 'Failed to upload PDF';

                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: errorMessage
                });
            });
    }

	private pollAndDownloadCsv(csvUrl: string, originalFileName: string, attempt: number = 1, maxAttempts: number = 30): void {
		const retryDelay = 5000;

		this.mlService.downloadCsv(csvUrl)
			.then(response => {
				if (response && response.body) {

					let csvFileName: string;
					if (originalFileName.toLowerCase().endsWith('.pdf')) {
						csvFileName = originalFileName.replace(/\.pdf$/i, '.csv');
					} else {
						csvFileName = originalFileName.endsWith('.csv')
							? originalFileName
							: `${originalFileName}.csv`;
					}

					const blob = new Blob([response.body], { type: 'text/csv' });
					const url = window['URL'].createObjectURL(blob);
					const link = document.createElement('a');
					link.href = url;
					link.download = csvFileName;
					link.style.display = 'none';
					document.body.appendChild(link);
					link.click();

					setTimeout(() => {
						document.body.removeChild(link);
						window['URL'].revokeObjectURL(url);
					}, 100);

					this.isUploading = false;
					this.removePdf();

					this.messageService.add({
						severity: 'info',
						summary: 'Download',
						detail: `CSV file "${csvFileName}" downloaded successfully`
					});
				} else {
					throw new Error('Empty response');
				}
			})
			.catch(error => {
				if (attempt < maxAttempts) {
					console.log(`CSV not ready yet, attempt ${attempt}/${maxAttempts}. Retrying in ${retryDelay / 1000} seconds...`);

					// Store timeout ID for cleanup
					const timeoutId = setTimeout(() => {
						this.pollAndDownloadCsv(csvUrl, originalFileName, attempt + 1, maxAttempts);
					}, retryDelay);
					this.retryTimeoutIds.push(timeoutId);
				} else {
					this.isUploading = false;
					this.removePdf();

					console.error('Failed to download CSV after all attempts:', error);
					this.messageService.add({
						severity: 'warn',
						summary: 'Download Warning',
						detail: `CSV file is taking longer than expected. You can try to access it manually: ${csvUrl}`
					});
				}
			});
	}

    getFileSize(bytes: number): string {
        if (bytes === 0) {return '0 Bytes';}
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    }
}
